/**
 * The three Bluetooth Low Energy namespaces exposed by ATBase, one per provider role.
 *
 * They are separate because the roles are separate objects natively with different lifecycles: the
 * radio and permissions, the scanner and GATT client, and the advertiser and GATT server.
 */
export const BluetoothLowEnergyNamespaces = {
    ADAPTER: 'bleAdapter',
    CENTRAL: 'bleCentral',
    PERIPHERAL: 'blePeripheral',
} as const;

export type BluetoothLowEnergyNamespace = (typeof BluetoothLowEnergyNamespaces)[keyof typeof BluetoothLowEnergyNamespaces];

/**
 * The environment seam for the Bluetooth Low Energy capability.
 *
 * Note that "adapter" carries two unrelated meanings around this capability, and both appear in these
 * file names. Here it is the adapter pattern — the object that knows how to reach the platform from
 * this environment. In `BluetoothLowEnergyAdapterService` it is the Bluetooth adapter, meaning the
 * radio itself.
 *
 * Unlike the camera and printing adapters, this one exposes a single generic entry point rather than
 * one abstract method per operation. The capability has 37 operations across the three roles, none of
 * which this layer inspects or transforms — the environment is the only thing that varies, so
 * enumerating all 37 in three separate files would triple the surface without adding a single check.
 * The named, documented vocabulary a caller actually uses lives in the three role services, which is
 * also where each operation's options are described.
 */
export abstract class AbstractBluetoothLowEnergyAdapter {
    /**
     * Invokes one Bluetooth Low Energy operation.
     *
     * @param namespace the provider role the operation belongs to.
     * @param action the operation name, e.g. 'startScan'.
     * @param parameters the operation's options object, if it takes one.
     */
    public abstract call(namespace: BluetoothLowEnergyNamespace, action: string, parameters?: any): Promise<any>;
}
